import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Code, CreditCard, Gift, Globe2, Link, Percent, Save, Tags } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSettings, SettingsState } from '../../hooks/useSettings';
import { useI18n } from '../../i18n/LanguageContext';

type ToggleProps = {
    label: string;
    helper?: string;
    value: boolean;
    onChange: (v: boolean) => void;
};

function ToggleRow({ label, helper, value, onChange }: ToggleProps) {
    return (
        <label className="flex items-start justify-between gap-4 py-2">
            <div>
                <p className="text-sm font-medium text-ink">{label}</p>
                {helper && <p className="text-xs text-slate-500">{helper}</p>}
            </div>
            <input
                type="checkbox"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
            />
        </label>
    );
}

const schedulingItOverrides: Record<string, string> = {
    'Provider Scheduling': 'Programmazione provider',
    'Recurring working hours, portal bookable windows, and date overrides now live inside each provider profile.':
        'Orari di lavoro ricorrenti, finestre prenotabili dal portale ed eccezioni per data ora vivono dentro ogni profilo provider.',
    'Open provider profiles': 'Apri profili provider',
    'General Settings': 'Impostazioni generali',
    'Default Slot Duration (minutes)': 'Durata slot predefinita (minuti)',
    'Default View': 'Vista predefinita',
    Day: 'Giorno',
    Week: 'Settimana',
    Month: 'Mese',
    'Allow Overbooking': 'Consenti overbooking',
    'Automation Settings': 'Impostazioni automazione',
    'Auto-assign room based on availability': 'Assegna automaticamente la stanza in base alla disponibilità',
    'Auto-assign provider assistant': 'Assegna automaticamente l’assistente del provider',
    'Auto-suggest appointment times for patients': 'Suggerisci automaticamente orari appuntamento ai pazienti',
    'Predictive no-show model': 'Modello predittivo no-show',
    'Predictive scheduling suggestions': 'Suggerimenti predittivi di programmazione',
    'Auto-fill provider availability from templates': 'Compila automaticamente la disponibilità provider dai template',
    'Auto-block time after high-intensity procedures': 'Blocca automaticamente tempo dopo procedure ad alta intensità',
    'AI appointment distribution balancing': 'Bilanciamento IA della distribuzione appuntamenti',
    'Calendar Views Settings': 'Impostazioni viste calendario',
    'Show weekends': 'Mostra weekend',
    'Show closed hours': 'Mostra ore chiuse',
    Theme: 'Tema',
    Light: 'Chiaro',
    Dark: 'Scuro',
    'Start week on': 'Inizio settimana',
    Monday: 'Lunedì',
    Sunday: 'Domenica',
    'Conflict Rules & Logic': 'Regole conflitti e logica',
    'Allow provider double-booking': 'Consenti doppia prenotazione provider',
    'Allow room double-booking': 'Consenti doppia prenotazione stanza',
    'Notifications & Reminders': 'Notifiche e promemoria',
    'Email reminder timing': 'Tempi promemoria email',
    'e.g. 24h before': 'es. 24h prima',
    'SMS reminder timing': 'Tempi promemoria SMS',
    'e.g. 2h before': 'es. 2h prima',
    'Twilio SMS reminders': 'Promemoria SMS Twilio',
    'Twilio from phone number': 'Numero mittente Twilio',
    'e.g. +15551234567': 'es. +15551234567',
    'Message templates': 'Template messaggi',
    'Welcome message': 'Messaggio di benvenuto',
    'Appointment confirmation': 'Conferma appuntamento',
    'Appointment reminder': 'Promemoria appuntamento',
    'Appointment cancellation': 'Cancellazione appuntamento',
    'Appointment reschedule': 'Riprogrammazione appuntamento',
    'Post-visit follow-up': 'Follow-up post-visita',
    'Available variables: {{organizationName}}, {{patientFirstName}}, {{patientName}}, {{providerName}}, {{appointmentType}}, {{appointmentTime}}, {{locationName}}':
        'Variabili disponibili: {{organizationName}}, {{patientFirstName}}, {{patientName}}, {{providerName}}, {{appointmentType}}, {{appointmentTime}}, {{locationName}}',
    'Telemed link delivery rules': 'Regole invio link telemed',
    'e.g. SMS + Email': 'es. SMS + Email',
    'Automated voice call reminders': 'Promemoria automatici con chiamata vocale',
    'Add-to-calendar support': 'Supporto aggiunta al calendario',
    'Send cancellation confirmations': 'Invia conferme di cancellazione',
    'Provider daily schedule email (6 AM)': 'Email agenda giornaliera provider (6:00)',
    'General Settings & Policies': 'Impostazioni generali e policy',
    'Cancellation Policy': 'Policy di cancellazione',
    'Enter cancellation policy details...': 'Inserisci i dettagli della policy di cancellazione...',
    'No-Show Policy': 'Policy no-show',
    'Enter no-show policy details...': 'Inserisci i dettagli della policy no-show...',
};

function createSchedulingTranslator(language: string, tr: (value: string) => string) {
    if (language !== 'it') return tr;
    return (phrase: string) => {
        const normalized = phrase.trim();
        if (!normalized) return phrase;
        const globalTranslation = tr(normalized);
        if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
        const local = schedulingItOverrides[normalized];
        return local ? phrase.replace(normalized, local) : phrase;
    };
}

function BookingControlCard({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
    return (
        <section className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-1 flex items-center gap-2 text-ink">
                {icon}
                <h4 className="text-sm font-semibold">{title}</h4>
            </div>
            {children}
        </section>
    );
}

function InputLike({ label, value, icon }: { label: string; value: string; icon?: ReactNode }) {
    return (
        <label className="space-y-1 text-sm">
            <span className="font-medium text-slate-500">{label}</span>
            <span className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700">
                {icon}
                {value}
            </span>
        </label>
    );
}

export default function SettingsScheduling() {
    const navigate = useNavigate();
    const { settings, setSettings, load, save, loading, error } = useSettings();
    const { language, tr, t } = useI18n();
    const ts = createSchedulingTranslator(language, tr);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [bookingWizardStep, setBookingWizardStep] = useState('Preconditions ready');
    const [reserveWithGoogle, setReserveWithGoogle] = useState(false);
    const [insuranceDuringBooking, setInsuranceDuringBooking] = useState(true);
    const [packageUpsell, setPackageUpsell] = useState(true);
    const [restrictedBooking, setRestrictedBooking] = useState(false);
    const [trackingSetup, setTrackingSetup] = useState(['Google Tag Manager', 'Analytics']);

    useEffect(() => {
        load();
    }, [load]);

    async function handleSave() {
        setSaving(true);
        setSuccess(null);
        try {
            await save(settings);
            setSuccess(ts('Saved successfully'));
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }

    function updateField(updater: (prev: SettingsState['scheduling']) => SettingsState['scheduling']) {
        setSettings((prev) => ({ ...prev, scheduling: updater(prev.scheduling) }));
    }

    function updateMessageTemplate(key: string, value: string) {
        updateField((prev) => ({
            ...prev,
            reminderMessageTemplates: {
                ...prev.reminderMessageTemplates,
                [key]: value,
            },
        }));
    }

    function addCalendarActivity() {
        const title = window.prompt(ts('Activity title'), ts('Calendar activity'));
        if (!title) return;
        updateField((prev) => ({
            ...prev,
            calendarActivities: [
                ...prev.calendarActivities,
                {
                    id: `activity-${Date.now()}`,
                    title: title.trim(),
                    type: ts('Task'),
                    date: new Date().toISOString().slice(0, 10),
                    owner: ts('Reception'),
                    status: ts('Open'),
                },
            ],
        }));
    }

    function updateCalendarActivity(id: string, patch: Partial<SettingsState['scheduling']['calendarActivities'][number]>) {
        updateField((prev) => ({
            ...prev,
            calendarActivities: prev.calendarActivities.map((activity) => activity.id === id ? { ...activity, ...patch } : activity),
        }));
    }

    function deleteCalendarActivity(id: string) {
        updateField((prev) => ({
            ...prev,
            calendarActivities: prev.calendarActivities.filter((activity) => activity.id !== id),
        }));
    }

    function updateAppointmentIcon(id: string, patch: Partial<SettingsState['scheduling']['appointmentIconLegend'][number]>) {
        updateField((prev) => ({
            ...prev,
            appointmentIconLegend: prev.appointmentIconLegend.map((icon) => icon.id === id ? { ...icon, ...patch } : icon),
        }));
    }

    function addShiftNote() {
        updateField((prev) => ({
            ...prev,
            shiftNotes: [
                ...prev.shiftNotes,
                { id: `shift-${Date.now()}`, date: new Date().toISOString().slice(0, 10), providerName: ts('Unassigned'), note: ts('New staff shift note') },
            ],
        }));
    }

    function updateShiftNote(id: string, patch: Partial<SettingsState['scheduling']['shiftNotes'][number]>) {
        updateField((prev) => ({
            ...prev,
            shiftNotes: prev.shiftNotes.map((note) => note.id === id ? { ...note, ...patch } : note),
        }));
    }

    function deleteShiftNote(id: string) {
        updateField((prev) => ({ ...prev, shiftNotes: prev.shiftNotes.filter((note) => note.id !== id) }));
    }

    function addDayNote() {
        updateField((prev) => ({
            ...prev,
            dayNotes: [
                ...prev.dayNotes,
                { id: `day-${Date.now()}`, date: new Date().toISOString().slice(0, 10), note: ts('New calendar day note') },
            ],
        }));
    }

    function updateDayNote(id: string, patch: Partial<SettingsState['scheduling']['dayNotes'][number]>) {
        updateField((prev) => ({
            ...prev,
            dayNotes: prev.dayNotes.map((note) => note.id === id ? { ...note, ...patch } : note),
        }));
    }

    function deleteDayNote(id: string) {
        updateField((prev) => ({ ...prev, dayNotes: prev.dayNotes.filter((note) => note.id !== id) }));
    }

    function addCalendarPreset() {
        updateField((prev) => ({
            ...prev,
            calendarPresets: [
                ...prev.calendarPresets,
                { id: `preset-${Date.now()}`, name: ts('New calendar preset'), view: prev.defaultView, displayMode: 'calendar', filterStatus: 'all', providerIds: [], locationIds: [] },
            ],
        }));
    }

    function updateCalendarPreset(id: string, patch: Partial<SettingsState['scheduling']['calendarPresets'][number]>) {
        updateField((prev) => ({
            ...prev,
            calendarPresets: prev.calendarPresets.map((preset) => preset.id === id ? { ...preset, ...patch } : preset),
        }));
    }

    function deleteCalendarPreset(id: string) {
        updateField((prev) => ({ ...prev, calendarPresets: prev.calendarPresets.filter((preset) => preset.id !== id) }));
    }

    function updateExternalCalendarSync(id: string, patch: Partial<SettingsState['scheduling']['externalCalendarSync'][number]>) {
        updateField((prev) => ({
            ...prev,
            externalCalendarSync: prev.externalCalendarSync.map((sync) => sync.id === id ? { ...sync, ...patch } : sync),
        }));
    }

    if (loading) return <div className="p-6">{t('common.loading', 'Loading...')}</div>;
    if (error) return <div className="p-6 text-rose-600">{ts('Error')}: {error}</div>;

    const s = settings.scheduling;

    return (
        <div className="space-y-6 pb-10">
            <div className="flex items-center justify-between sticky top-0 bg-slate-50/80 backdrop-blur-sm py-4 z-10 border-b border-slate-200/50 -mx-6 px-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                        <ArrowLeft size={20} className="text-slate-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-ink">{ts('Scheduling Settings')}</h1>
                        <p className="text-slate-500 text-sm">{ts('Control automation, calendar visibility, conflicts, and reminders.')}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {success && <span className="text-emerald-600 text-sm font-medium animate-in fade-in slide-in-from-right-4">{success}</span>}
                    <button
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ink text-white shadow-lg shadow-ink/20 hover:bg-ink/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        <Save size={18} /> {saving ? ts('Saving...') : ts('Save Changes')}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 max-w-6xl w-full">
                <div className="card p-5 space-y-3">
                    <h3 className="font-semibold text-ink">{ts('Provider Scheduling')}</h3>
                    <p className="text-sm text-slate-500">
                        {ts('Recurring working hours, portal bookable windows, and date overrides now live inside each provider profile.')}
                    </p>
                    <div>
                        <button
                            type="button"
                            onClick={() => navigate('/settings/providers')}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        >
                            {ts('Open provider profiles')}
                        </button>
                    </div>
                </div>

                <div className="card p-5 space-y-4">
                    <h3 className="font-semibold text-ink">{ts('Online Booking Setup')}</h3>
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                        <BookingControlCard title={ts('Setup wizard')} icon={<Globe2 size={18} />}>
                            {['Preconditions ready', 'Services visible', 'Team assigned', 'Payments enabled'].map((step) => (
                                <button key={step} type="button" className={`rounded-lg border px-3 py-2 text-left text-sm ${bookingWizardStep === step ? 'border-ink bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700'}`} onClick={() => setBookingWizardStep(step)}>
                                    {ts(step)}
                                </button>
                            ))}
                        </BookingControlCard>
                        <BookingControlCard title={ts('Embed and integrations')} icon={<Code size={18} />}>
                            <textarea className="min-h-20 rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs" readOnly value={'<script src="https://booking.elen1.app/widget.js" data-clinic="elen"></script>'} />
                            <ToggleRow label={ts('Reserve with Google')} value={reserveWithGoogle} onChange={setReserveWithGoogle} />
                            <InputLike label={ts('Custom confirmation redirect')} value="https://clinic.example.com/thanks" />
                        </BookingControlCard>
                        <BookingControlCard title={ts('Pricing and payments')} icon={<CreditCard size={18} />}>
                            <ToggleRow label={ts('Smart pricing rules')} value={s.aiDistributionBalancing} onChange={(v) => updateField((prev) => ({ ...prev, aiDistributionBalancing: v }))} />
                            <ToggleRow label={ts('Insurance payment during booking')} value={insuranceDuringBooking} onChange={setInsuranceDuringBooking} />
                            <ToggleRow label={ts('Package upsell')} value={packageUpsell} onChange={setPackageUpsell} />
                        </BookingControlCard>
                        <BookingControlCard title={ts('Booking customization')} icon={<Tags size={18} />}>
                            {['General', 'Services', 'Locations', 'Team', 'Booking details'].map((item) => (
                                <div key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">{ts(item)}</div>
                            ))}
                        </BookingControlCard>
                        <BookingControlCard title={ts('Rules, rooms, and terms')} icon={<Link size={18} />}>
                            <ToggleRow label={ts('Room availability for online bookings')} value={s.autoAssignRoom} onChange={(v) => updateField((prev) => ({ ...prev, autoAssignRoom: v }))} />
                            <ToggleRow label={ts('Restricted patient booking rules')} value={restrictedBooking} onChange={setRestrictedBooking} />
                            <textarea className="min-h-20 rounded-lg border border-slate-200 px-3 py-2 text-sm" defaultValue="Terms and conditions shown before booking confirmation." />
                        </BookingControlCard>
                        <BookingControlCard title={ts('Promo, tracking, portal')} icon={<Percent size={18} />}>
                            <InputLike label={ts('Promo code')} value="WELCOME10" icon={<Gift size={14} />} />
                            <div className="flex flex-wrap gap-2">
                                {['Google Tag Manager', 'Analytics', 'Meta Pixel'].map((item) => (
                                    <button key={item} type="button" className={`rounded-full border px-3 py-1 text-xs ${trackingSetup.includes(item) ? 'border-ink bg-ink text-white' : 'border-slate-200 bg-white text-slate-600'}`} onClick={() => setTrackingSetup((current) => current.includes(item) ? current.filter((value) => value !== item) : [...current, item])}>
                                        {item}
                                    </button>
                                ))}
                            </div>
                            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">{ts('Membership view in client portal')}</div>
                        </BookingControlCard>
                    </div>
                </div>

                <div className="card p-5 space-y-4">
                    <h3 className="font-semibold text-ink">{ts('General Settings')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('Default Slot Duration (minutes)')}</span>
                            <input
                                type="number"
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none"
                                value={s.defaultSlotMinutes}
                                onChange={(e) => updateField((prev) => ({ ...prev, defaultSlotMinutes: parseInt(e.target.value) || 30 }))}
                            />
                        </label>
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('Default View')}</span>
                            <select
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none"
                                value={s.defaultView}
                                onChange={(e) => updateField((prev) => ({ ...prev, defaultView: e.target.value as any }))}
                            >
                                <option value="day">{ts('Day')}</option>
                                <option value="week">{ts('Week')}</option>
                                <option value="month">{ts('Month')}</option>
                            </select>
                        </label>
                        <div className="flex items-center gap-2 pt-6">
                            <input
                                type="checkbox"
                                id="overbooking"
                                checked={s.overbookingAllowed}
                                onChange={(e) => updateField((prev) => ({ ...prev, overbookingAllowed: e.target.checked }))}
                                className="rounded border-slate-300 text-ink focus:ring-ink"
                            />
                            <label htmlFor="overbooking" className="text-sm text-slate-700">{ts('Allow Overbooking')}</label>
                        </div>
                    </div>
                </div>

                <div className="card p-5 space-y-2">
                    <h3 className="font-semibold text-ink">{ts('Automation Settings')}</h3>
                    <ToggleRow label={ts('Auto-assign room based on availability')} value={s.autoAssignRoom} onChange={(v) => updateField((prev) => ({ ...prev, autoAssignRoom: v }))} />
                    <ToggleRow label={ts('Auto-assign provider assistant')} value={s.autoAssignAssistant} onChange={(v) => updateField((prev) => ({ ...prev, autoAssignAssistant: v }))} />
                    <ToggleRow label={ts('Auto-suggest appointment times for patients')} value={s.autoSuggestTimes} onChange={(v) => updateField((prev) => ({ ...prev, autoSuggestTimes: v }))} />
                    <ToggleRow label={ts('Predictive no-show model')} value={s.predictiveNoShowModel} onChange={(v) => updateField((prev) => ({ ...prev, predictiveNoShowModel: v }))} />
                    <ToggleRow label={ts('Predictive scheduling suggestions')} value={s.predictiveSchedulingSuggestions} onChange={(v) => updateField((prev) => ({ ...prev, predictiveSchedulingSuggestions: v }))} />
                    <ToggleRow label={ts('Auto-fill provider availability from templates')} value={s.autofillAvailabilityFromTemplates} onChange={(v) => updateField((prev) => ({ ...prev, autofillAvailabilityFromTemplates: v }))} />
                    <ToggleRow label={ts('Auto-block time after high-intensity procedures')} value={s.autoBlockAfterHighIntensity} onChange={(v) => updateField((prev) => ({ ...prev, autoBlockAfterHighIntensity: v }))} />
                    <ToggleRow label={ts('AI appointment distribution balancing')} value={s.aiDistributionBalancing} onChange={(v) => updateField((prev) => ({ ...prev, aiDistributionBalancing: v }))} />
                </div>

                <div className="card p-5 space-y-4">
                    <h3 className="font-semibold text-ink">{ts('Calendar Views Settings')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ToggleRow label={ts('Show weekends')} value={s.showWeekends} onChange={(v) => updateField((prev) => ({ ...prev, showWeekends: v }))} />
                        <ToggleRow label={ts('Show closed hours')} value={s.showClosedHours} onChange={(v) => updateField((prev) => ({ ...prev, showClosedHours: v }))} />
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('Theme')}</span>
                            <select
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none"
                                value={s.themeMode}
                                onChange={(e) => updateField((prev) => ({ ...prev, themeMode: e.target.value as 'light' | 'dark' }))}
                            >
                                <option value="light">{ts('Light')}</option>
                                <option value="dark">{ts('Dark')}</option>
                            </select>
                        </label>
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('Start week on')}</span>
                            <select
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none"
                                value={s.weekStart}
                                onChange={(e) => updateField((prev) => ({ ...prev, weekStart: e.target.value as 'sunday' | 'monday' }))}
                            >
                                <option value="monday">{ts('Monday')}</option>
                                <option value="sunday">{ts('Sunday')}</option>
                            </select>
                        </label>
                    </div>
                </div>

                <div className="card p-5 space-y-4">
                    <h3 className="font-semibold text-ink">{ts('Calendar — General')}</h3>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <section className="rounded-xl border border-slate-200 bg-white p-4">
                            <h4 className="text-sm font-semibold text-ink">{ts('Calendar Overview')}</h4>
                            <p className="mt-1 text-sm text-slate-500">{ts('Control the main clinic schedule view, appointment indicators, locking, and passcode rules.')}</p>
                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                <div className="rounded-lg bg-slate-50 p-3">{ts('Default View')}: <strong>{ts(s.defaultView)}</strong></div>
                                <div className="rounded-lg bg-slate-50 p-3">{ts('Default Slot Duration (minutes)')}: <strong>{s.defaultSlotMinutes}</strong></div>
                            </div>
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('Calendar Locking')}</h4>
                            <ToggleRow label={ts('Enable calendar screen lock')} helper={ts('Staff must enter the configured passcode before editing a locked calendar.')} value={s.calendarLockingEnabled} onChange={(v) => updateField((prev) => ({ ...prev, calendarLockingEnabled: v }))} />
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Calendar passcode')}</span>
                                <input type="password" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarLockPasscode} onChange={(event) => updateField((prev) => ({ ...prev, calendarLockPasscode: event.target.value }))} />
                            </label>
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('Calendar Passcode Rules')}</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="text-sm space-y-1 block">
                                    <span className="text-slate-500 font-medium">{ts('Minimum length')}</span>
                                    <input type="number" min={4} max={12} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarPasscodeRules.minLength} onChange={(event) => updateField((prev) => ({ ...prev, calendarPasscodeRules: { ...prev.calendarPasscodeRules, minLength: Number(event.target.value) || 4 } }))} />
                                </label>
                                <label className="text-sm space-y-1 block">
                                    <span className="text-slate-500 font-medium">{ts('Idle timeout minutes')}</span>
                                    <input type="number" min={1} max={240} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarPasscodeRules.idleTimeoutMinutes} onChange={(event) => updateField((prev) => ({ ...prev, calendarPasscodeRules: { ...prev.calendarPasscodeRules, idleTimeoutMinutes: Number(event.target.value) || 15 } }))} />
                                </label>
                            </div>
                            <ToggleRow label={ts('Require numbers')} value={s.calendarPasscodeRules.requireNumbers} onChange={(v) => updateField((prev) => ({ ...prev, calendarPasscodeRules: { ...prev.calendarPasscodeRules, requireNumbers: v } }))} />
                            <ToggleRow label={ts('Lock on idle')} value={s.calendarPasscodeRules.lockOnIdle} onChange={(v) => updateField((prev) => ({ ...prev, calendarPasscodeRules: { ...prev.calendarPasscodeRules, lockOnIdle: v } }))} />
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <h4 className="text-sm font-semibold text-ink">{ts('Calendar Activities')}</h4>
                                <button type="button" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={addCalendarActivity}>{ts('Create')}</button>
                            </div>
                            <div className="space-y-2">
                                {s.calendarActivities.map((activity) => (
                                    <div key={activity.id} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[1fr_0.8fr_auto]">
                                        <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={activity.title} onChange={(event) => updateCalendarActivity(activity.id, { title: event.target.value })} />
                                        <input type="date" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={activity.date} onChange={(event) => updateCalendarActivity(activity.id, { date: event.target.value })} />
                                        <button type="button" className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => deleteCalendarActivity(activity.id)}>{ts('Delete')}</button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                    <section className="rounded-xl border border-slate-200 bg-white p-4">
                        <h4 className="text-sm font-semibold text-ink">{ts('Appointment Icons')}</h4>
                        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                            {s.appointmentIconLegend.map((icon) => (
                                <div key={icon.id} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[auto_1fr_auto]">
                                    <input type="color" className="h-10 w-12 rounded-lg border border-slate-200 bg-white p-1" value={icon.color} onChange={(event) => updateAppointmentIcon(icon.id, { color: event.target.value })} />
                                    <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={icon.label} onChange={(event) => updateAppointmentIcon(icon.id, { label: event.target.value })} />
                                    <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700">
                                        <input type="checkbox" checked={icon.enabled} onChange={(event) => updateAppointmentIcon(icon.id, { enabled: event.target.checked })} className="accent-ink" />
                                        {ts('Show')}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                <div className="card p-5 space-y-4">
                    <h3 className="font-semibold text-ink">{ts('Calendar Settings')}</h3>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('Appointment Settings')}</h4>
                            <ToggleRow label={ts('Require appointment service')} value={s.appointmentSettings.requireService} onChange={(v) => updateField((prev) => ({ ...prev, appointmentSettings: { ...prev.appointmentSettings, requireService: v } }))} />
                            <ToggleRow label={ts('Require appointment room')} value={s.appointmentSettings.requireRoom} onChange={(v) => updateField((prev) => ({ ...prev, appointmentSettings: { ...prev.appointmentSettings, requireRoom: v } }))} />
                            <ToggleRow label={ts('Client Card Access')} value={s.appointmentSettings.allowClientCardAccess} onChange={(v) => updateField((prev) => ({ ...prev, appointmentSettings: { ...prev.appointmentSettings, allowClientCardAccess: v } }))} />
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Default appointment status')}</span>
                                <input className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.appointmentSettings.defaultStatus} onChange={(event) => updateField((prev) => ({ ...prev, appointmentSettings: { ...prev.appointmentSettings, defaultStatus: event.target.value } }))} />
                            </label>
                            <textarea className="min-h-20 rounded-lg border border-slate-200 px-3 py-2 text-sm" value={s.appointmentSettings.bookingRuleSummary} onChange={(event) => updateField((prev) => ({ ...prev, appointmentSettings: { ...prev.appointmentSettings, bookingRuleSummary: event.target.value } }))} />
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('Calendar Configuration')}</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="text-sm space-y-1 block">
                                    <span className="text-slate-500 font-medium">{ts('Visible start hour')}</span>
                                    <input type="number" min={0} max={23} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarConfiguration.visibleHoursStart} onChange={(event) => updateField((prev) => ({ ...prev, calendarConfiguration: { ...prev.calendarConfiguration, visibleHoursStart: Number(event.target.value) || 0 } }))} />
                                </label>
                                <label className="text-sm space-y-1 block">
                                    <span className="text-slate-500 font-medium">{ts('Visible end hour')}</span>
                                    <input type="number" min={1} max={24} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarConfiguration.visibleHoursEnd} onChange={(event) => updateField((prev) => ({ ...prev, calendarConfiguration: { ...prev.calendarConfiguration, visibleHoursEnd: Number(event.target.value) || 18 } }))} />
                                </label>
                            </div>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Default display mode')}</span>
                                <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarConfiguration.defaultDisplayMode} onChange={(event) => updateField((prev) => ({ ...prev, calendarConfiguration: { ...prev.calendarConfiguration, defaultDisplayMode: event.target.value as 'calendar' | 'table' } }))}>
                                    <option value="calendar">{ts('Calendar')}</option>
                                    <option value="table">{ts('Table')}</option>
                                </select>
                            </label>
                            <ToggleRow label={ts('Show provider avatars')} value={s.calendarConfiguration.showProviderAvatars} onChange={(v) => updateField((prev) => ({ ...prev, calendarConfiguration: { ...prev.calendarConfiguration, showProviderAvatars: v } }))} />
                            <ToggleRow label={ts('Show location filter')} value={s.calendarConfiguration.showLocationFilter} onChange={(v) => updateField((prev) => ({ ...prev, calendarConfiguration: { ...prev.calendarConfiguration, showLocationFilter: v } }))} />
                            <ToggleRow label={ts('Multi-Location View')} value={s.calendarFilters.multiLocationEnabled} onChange={(v) => updateField((prev) => ({ ...prev, calendarFilters: { ...prev.calendarFilters, multiLocationEnabled: v } }))} />
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('Calendar Appearance')}</h4>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Density')}</span>
                                <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarAppearance.density} onChange={(event) => updateField((prev) => ({ ...prev, calendarAppearance: { ...prev.calendarAppearance, density: event.target.value as 'comfortable' | 'compact' } }))}>
                                    <option value="comfortable">{ts('Comfortable')}</option>
                                    <option value="compact">{ts('Compact')}</option>
                                </select>
                            </label>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Color appointments by')}</span>
                                <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarAppearance.colorMode} onChange={(event) => updateField((prev) => ({ ...prev, calendarAppearance: { ...prev.calendarAppearance, colorMode: event.target.value as 'service' | 'provider' | 'status' } }))}>
                                    <option value="service">{ts('Service')}</option>
                                    <option value="provider">{ts('Provider')}</option>
                                    <option value="status">{ts('Status')}</option>
                                </select>
                            </label>
                            <ToggleRow label={ts('Show cancelled appointments')} value={s.calendarAppearance.showCancelledAppointments} onChange={(v) => updateField((prev) => ({ ...prev, calendarAppearance: { ...prev.calendarAppearance, showCancelledAppointments: v } }))} />
                            <ToggleRow label={ts('Show appointment legend')} value={s.calendarAppearance.showAppointmentLegend} onChange={(v) => updateField((prev) => ({ ...prev, calendarAppearance: { ...prev.calendarAppearance, showAppointmentLegend: v } }))} />
                            <ToggleRow label={ts('Appointment Overlap')} value={s.appointmentOverlapEnabled} onChange={(v) => updateField((prev) => ({ ...prev, appointmentOverlapEnabled: v }))} />
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <h4 className="text-sm font-semibold text-ink">{ts('Calendar Presets')}</h4>
                                <button type="button" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={addCalendarPreset}>{ts('Create')}</button>
                            </div>
                            {s.calendarPresets.map((preset) => (
                                <div key={preset.id} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[1fr_0.7fr_auto]">
                                    <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={preset.name} onChange={(event) => updateCalendarPreset(preset.id, { name: event.target.value })} />
                                    <select className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={preset.view} onChange={(event) => updateCalendarPreset(preset.id, { view: event.target.value as 'day' | 'week' | 'month' })}>
                                        <option value="day">{ts('Day')}</option>
                                        <option value="week">{ts('Week')}</option>
                                        <option value="month">{ts('Month')}</option>
                                    </select>
                                    <button type="button" className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => deleteCalendarPreset(preset.id)}>{ts('Delete')}</button>
                                </div>
                            ))}
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('Column Ordering')}</h4>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Table columns')}</span>
                                <input className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarColumnOrder.tableColumns.join(', ')} onChange={(event) => updateField((prev) => ({ ...prev, calendarColumnOrder: { ...prev.calendarColumnOrder, tableColumns: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) } }))} />
                            </label>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Provider column IDs')}</span>
                                <input className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarColumnOrder.providerIds.join(', ')} onChange={(event) => updateField((prev) => ({ ...prev, calendarColumnOrder: { ...prev.calendarColumnOrder, providerIds: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) } }))} />
                            </label>
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('Calendar Filters')}</h4>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Staff IDs')}</span>
                                <input className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarFilters.staffIds.join(', ')} onChange={(event) => updateField((prev) => ({ ...prev, calendarFilters: { ...prev.calendarFilters, staffIds: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) } }))} />
                            </label>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Service IDs')}</span>
                                <input className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarFilters.serviceIds.join(', ')} onChange={(event) => updateField((prev) => ({ ...prev, calendarFilters: { ...prev.calendarFilters, serviceIds: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) } }))} />
                            </label>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{ts('Location IDs')}</span>
                                <input className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={s.calendarFilters.locationIds.join(', ')} onChange={(event) => updateField((prev) => ({ ...prev, calendarFilters: { ...prev.calendarFilters, locationIds: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) } }))} />
                            </label>
                        </section>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <h4 className="text-sm font-semibold text-ink">{ts('Shift Notes')}</h4>
                                <button type="button" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={addShiftNote}>{ts('Add')}</button>
                            </div>
                            {s.shiftNotes.map((note) => (
                                <div key={note.id} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[0.8fr_1fr_auto]">
                                    <input type="date" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={note.date} onChange={(event) => updateShiftNote(note.id, { date: event.target.value })} />
                                    <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={note.note} onChange={(event) => updateShiftNote(note.id, { note: event.target.value })} />
                                    <button type="button" className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => deleteShiftNote(note.id)}>{ts('Delete')}</button>
                                </div>
                            ))}
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <h4 className="text-sm font-semibold text-ink">{ts('Day Notes')}</h4>
                                <button type="button" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={addDayNote}>{ts('Add')}</button>
                            </div>
                            {s.dayNotes.map((note) => (
                                <div key={note.id} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[0.8fr_1fr_auto]">
                                    <input type="date" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={note.date} onChange={(event) => updateDayNote(note.id, { date: event.target.value })} />
                                    <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={note.note} onChange={(event) => updateDayNote(note.id, { note: event.target.value })} />
                                    <button type="button" className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => deleteDayNote(note.id)}>{ts('Delete')}</button>
                                </div>
                            ))}
                        </section>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('External Calendar Sync')}</h4>
                            {s.externalCalendarSync.map((sync) => (
                                <div key={sync.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3 space-y-2">
                                    <ToggleRow label={ts(sync.provider)} value={sync.enabled} onChange={(enabled) => updateExternalCalendarSync(sync.id, { enabled })} />
                                    <div className="grid grid-cols-2 gap-2">
                                        <select className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={sync.mode} onChange={(event) => updateExternalCalendarSync(sync.id, { mode: event.target.value as 'one-way' | 'two-way' })}>
                                            <option value="one-way">{ts('One-way')}</option>
                                            <option value="two-way">{ts('Two-way')}</option>
                                        </select>
                                        <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={sync.accountLabel} placeholder={ts('Account label')} onChange={(event) => updateExternalCalendarSync(sync.id, { accountLabel: event.target.value })} />
                                    </div>
                                </div>
                            ))}
                        </section>
                        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-ink">{ts('Clinic List')}</h4>
                            <ToggleRow label={ts('Enable clinic list')} value={s.clinicListSettings.enabled} onChange={(v) => updateField((prev) => ({ ...prev, clinicListSettings: { ...prev.clinicListSettings, enabled: v } }))} />
                            <ToggleRow label={ts('Open clinic list by default')} value={s.clinicListSettings.defaultOpen} onChange={(v) => updateField((prev) => ({ ...prev, clinicListSettings: { ...prev.clinicListSettings, defaultOpen: v } }))} />
                            <ToggleRow label={ts('Include status')} value={s.clinicListSettings.includeStatus} onChange={(v) => updateField((prev) => ({ ...prev, clinicListSettings: { ...prev.clinicListSettings, includeStatus: v } }))} />
                            <ToggleRow label={ts('Include service')} value={s.clinicListSettings.includeService} onChange={(v) => updateField((prev) => ({ ...prev, clinicListSettings: { ...prev.clinicListSettings, includeService: v } }))} />
                            <ToggleRow label={ts('Include location')} value={s.clinicListSettings.includeLocation} onChange={(v) => updateField((prev) => ({ ...prev, clinicListSettings: { ...prev.clinicListSettings, includeLocation: v } }))} />
                        </section>
                    </div>
                </div>

                <div className="card p-5 space-y-2">
                    <h3 className="font-semibold text-ink">{ts('Conflict Rules & Logic')}</h3>
                    <ToggleRow label={ts('Allow provider double-booking')} value={s.allowProviderDoubleBooking} onChange={(v) => updateField((prev) => ({ ...prev, allowProviderDoubleBooking: v }))} />
                    <ToggleRow label={ts('Allow room double-booking')} value={s.allowRoomDoubleBooking} onChange={(v) => updateField((prev) => ({ ...prev, allowRoomDoubleBooking: v }))} />
                </div>

                <div className="card p-5 space-y-4">
                    <h3 className="font-semibold text-ink">{ts('Notifications & Reminders')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('Email reminder timing')}</span>
                            <input
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                                value={s.emailReminderTiming}
                                onChange={(e) => updateField((prev) => ({ ...prev, emailReminderTiming: e.target.value }))}
                                placeholder={ts('e.g. 24h before')}
                            />
                        </label>
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('SMS reminder timing')}</span>
                            <input
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                                value={s.smsReminderTiming}
                                onChange={(e) => updateField((prev) => ({ ...prev, smsReminderTiming: e.target.value }))}
                                placeholder={ts('e.g. 2h before')}
                            />
                        </label>
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('Telemed link delivery rules')}</span>
                            <input
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                                value={s.telemedLinkDeliveryRules}
                                onChange={(e) => updateField((prev) => ({ ...prev, telemedLinkDeliveryRules: e.target.value }))}
                                placeholder={ts('e.g. SMS + Email')}
                            />
                        </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ToggleRow label={ts('Twilio SMS reminders')} helper={ts('Requires TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN in the backend environment.')} value={s.twilioMessagingEnabled} onChange={(v) => updateField((prev) => ({ ...prev, twilioMessagingEnabled: v }))} />
                        <ToggleRow label={ts('WhatsApp reminders')} helper={ts('Sends appointment reminders over WhatsApp (Twilio). Requires a WhatsApp-enabled sender number.')} value={s.whatsappMessagingEnabled} onChange={(v) => updateField((prev) => ({ ...prev, whatsappMessagingEnabled: v }))} />
                        <ToggleRow label={ts('Automated voice call reminders')} value={s.voiceReminderEnabled} onChange={(v) => updateField((prev) => ({ ...prev, voiceReminderEnabled: v }))} />
                        <ToggleRow label={ts('Add-to-calendar support')} value={s.addToCalendarSupport} onChange={(v) => updateField((prev) => ({ ...prev, addToCalendarSupport: v }))} />
                        <ToggleRow label={ts('Send cancellation confirmations')} value={s.cancellationConfirmations} onChange={(v) => updateField((prev) => ({ ...prev, cancellationConfirmations: v }))} />
                        <ToggleRow label={ts('Provider daily schedule email (6 AM)')} value={s.providerDailyScheduleEmail} onChange={(v) => updateField((prev) => ({ ...prev, providerDailyScheduleEmail: v }))} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('Twilio from phone number')}</span>
                            <input
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                                value={s.twilioFromPhoneNumber}
                                onChange={(e) => updateField((prev) => ({ ...prev, twilioFromPhoneNumber: e.target.value }))}
                                placeholder={ts('e.g. +15551234567')}
                            />
                        </label>
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('WhatsApp from number')}</span>
                            <input
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                                value={s.whatsappFromPhoneNumber}
                                onChange={(e) => updateField((prev) => ({ ...prev, whatsappFromPhoneNumber: e.target.value }))}
                                placeholder={ts('e.g. +15551234567')}
                            />
                        </label>
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium">{ts('WhatsApp reminder timing')}</span>
                            <input
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                                value={s.whatsappReminderTiming}
                                onChange={(e) => updateField((prev) => ({ ...prev, whatsappReminderTiming: e.target.value }))}
                                placeholder={ts('e.g. 24h before')}
                            />
                        </label>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <h4 className="text-sm font-semibold text-ink">{ts('Message templates')}</h4>
                            <p className="text-xs text-slate-500">
                                {ts('Available variables: {{organizationName}}, {{patientFirstName}}, {{patientName}}, {{providerName}}, {{appointmentType}}, {{appointmentTime}}, {{locationName}}')}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            {[
                                ['welcome', 'Welcome message'],
                                ['appointmentConfirmation', 'Appointment confirmation'],
                                ['appointmentReminder', 'Appointment reminder'],
                                ['appointmentCancellation', 'Appointment cancellation'],
                                ['appointmentReschedule', 'Appointment reschedule'],
                                ['postVisitFollowUp', 'Post-visit follow-up'],
                            ].map(([key, label]) => (
                                <label key={key} className="text-sm space-y-1 block">
                                    <span className="text-slate-500 font-medium">{ts(label)}</span>
                                    <textarea
                                        className="w-full min-h-24 border border-slate-200 rounded-lg px-3 py-2 bg-white resize-y"
                                        value={s.reminderMessageTemplates[key] ?? ''}
                                        onChange={(e) => updateMessageTemplate(key, e.target.value)}
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card p-5 space-y-4">
                    <h3 className="font-semibold text-ink">{ts('General Settings & Policies')}</h3>
                    <label className="text-sm space-y-1 block">
                        <span className="text-slate-500 font-medium">{ts('Cancellation Policy')}</span>
                        <textarea
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white h-24 resize-none focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none"
                            value={s.cancellationPolicy}
                            onChange={(e) => updateField((prev) => ({ ...prev, cancellationPolicy: e.target.value }))}
                            placeholder={ts('Enter cancellation policy details...')}
                        />
                    </label>
                    <label className="text-sm space-y-1 block">
                        <span className="text-slate-500 font-medium">{ts('No-Show Policy')}</span>
                        <textarea
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white h-24 resize-none focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none"
                            value={s.noShowPolicy}
                            onChange={(e) => updateField((prev) => ({ ...prev, noShowPolicy: e.target.value }))}
                            placeholder={ts('Enter no-show policy details...')}
                        />
                    </label>
                </div>
            </div>
        </div>
    );
}
